/**
 * URL validation for seller-supplied access links.
 *
 * WHAT ACTUALLY USES THIS TODAY. Nothing fetches these URLs. Every call site is
 * a rendering or form-validation gate: the product create/edit forms reject a
 * bad link at save time, and download-dialog.tsx re-checks at RENDER time before
 * turning one into an anchor, because a legacy or imported row never passed
 * through the forms. So the property being enforced is "safe to put in an href"
 * — an http(s) scheme, which is what keeps `javascript:` and `data:` out.
 *
 * The header here used to claim these values "get fetched or surfaced by
 * server-side code paths", which was not true and was the dangerous direction to
 * be wrong in: it invited a future server-side fetcher to treat this as an
 * SSRF gate it had already been given. It is not one on its own — DNS resolution
 * happens after this check, so a hostname that resolves to a private address
 * passes freely. The private/loopback/metadata rejections below are kept because
 * they are still worth having on a link shown to a buyer, and because they make
 * this a usable FIRST filter, but a real server-side fetcher needs to re-check
 * the resolved address at connect time, exactly as /api/image-proxy does by
 * pinning the bucket and refusing redirects.
 */

const BLOCKED_HOSTNAMES = new Set([
  'localhost',
  'ip6-localhost',
  'ip6-loopback',
  'metadata.google.internal',
  'metadata',
]);

function isBlockedIPv4(host: string): boolean {
  // 10.0.0.0/8, 127.0.0.0/8, 169.254.0.0/16, 172.16.0.0/12, 192.168.0.0/16,
  // 0.0.0.0/8, 100.64.0.0/10 (CGNAT), 224.0.0.0/4 (multicast), 240.0.0.0/4
  const m = host.match(/^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})$/);
  if (!m) return false;
  const [a, b] = [parseInt(m[1], 10), parseInt(m[2], 10)];
  if (a === 10 || a === 127 || a === 0) return true;
  if (a === 169 && b === 254) return true;
  if (a === 172 && b >= 16 && b <= 31) return true;
  if (a === 192 && b === 168) return true;
  if (a === 100 && b >= 64 && b <= 127) return true;
  if (a >= 224) return true;
  return false;
}

/**
 * Whether a BRACKETED IPv6 literal (`[::1]`, `[fd00::1]`) names a private,
 * loopback, link-local or multicast address.
 *
 * Callers must have established that the host really is an address — the prefix
 * tests below are substring matches on hex nibbles, and hostnames are made of
 * the same letters, so handing this a domain name gets a confident wrong answer.
 * `isValidUrl` gates on the leading bracket for exactly that reason.
 */
function isBlockedIPv6(host: string): boolean {
  const h = host.replace(/^\[|\]$/g, '').toLowerCase();
  if (h === '::1' || h === '::' || h === '0:0:0:0:0:0:0:1') return true;
  if (h.startsWith('fc') || h.startsWith('fd')) return true; // fc00::/7
  if (h.startsWith('fe8') || h.startsWith('fe9') || h.startsWith('fea') || h.startsWith('feb')) return true; // fe80::/10
  if (h.startsWith('ff')) return true; // multicast
  // IPv4-mapped / IPv4-compatible embeddings (::ffff:127.0.0.1, ::ffff:7f00:1,
  // ::127.0.0.1). These reach the SAME address as the dotted-quad form, so
  // without this the entire isBlockedIPv4 table above could be walked around by
  // spelling the target as IPv6. The WHATWG parser normalises the trailing quad
  // to hex, so both spellings have to be handled: pull the last 32 bits back out
  // and re-run the v4 rules on them.
  const embedded = ipv4FromMappedIPv6(h);
  if (embedded) return isBlockedIPv4(embedded);
  return false;
}

/**
 * The dotted-quad hidden inside an IPv4-mapped or IPv4-compatible IPv6 address,
 * or null when there isn't one.
 *
 * Accepts both spellings that reach a browser or Node: the literal trailing quad
 * (`::ffff:127.0.0.1`) and the hex form the URL parser normalises it to
 * (`::ffff:7f00:1`).
 */
function ipv4FromMappedIPv6(h: string): string | null {
  const mapped = /^::(?:ffff:)?(?:0:)?(.+)$/.exec(h);
  if (!mapped) return null;
  const tail = mapped[1];

  // Literal trailing quad.
  if (/^\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}$/.test(tail)) return tail;

  // Two hex groups carrying the 32 bits, e.g. 7f00:1 -> 127.0.0.1.
  const hex = /^([0-9a-f]{1,4}):([0-9a-f]{1,4})$/.exec(tail);
  if (!hex) return null;
  const high = parseInt(hex[1], 16);
  const low = parseInt(hex[2], 16);
  if (!Number.isFinite(high) || !Number.isFinite(low)) return null;
  return [high >> 8, high & 0xff, low >> 8, low & 0xff].join('.');
}

/**
 * Returns true when `raw` is a syntactically valid http(s) URL that does not
 * point at a private/loopback/metadata host. Empty strings return false — use
 * alongside a `.trim()` check if you need to treat empty inputs as "not yet
 * filled" rather than "invalid".
 */
export function isValidUrl(raw: string): boolean {
    const val = (raw ?? '').trim();
    if (!val) return false;
    try {
        const u = new URL(val);
        if (u.protocol !== 'http:' && u.protocol !== 'https:') return false;
        const host = u.hostname.toLowerCase();
        if (!host) return false;

        if (BLOCKED_HOSTNAMES.has(host)) return false;
        if (host.endsWith('.local') || host.endsWith('.internal')) return false;
        if (isBlockedIPv4(host)) return false;
        // BRACKETED HOSTS ONLY, and the gate is the whole point. The WHATWG
        // parser returns an IPv6 literal wrapped in brackets ("[::1]") and every
        // other host bare, so the brackets are what tells an ADDRESS from a NAME.
        // Run unguarded, the prefix table below reads ordinary domains as
        // addresses: "fc", "fd", "fe8".."feb" and "ff" are all real registrable
        // prefixes, so ffmpeg.org, fdic.gov, fdroid.org and fcbarcelona.com were
        // all rejected as private IPv6 ranges. That failed in both directions at
        // once — the seller could not save the link, and download-dialog.tsx
        // re-checks at RENDER time, so any such link already stored showed the
        // buyer a disabled "Unsafe link" button on something they had paid for.
        if (host.startsWith('[') && isBlockedIPv6(host)) return false;

        // Require a non-empty hostname with at least one dot (rules out things like "http://x").
        // IPv4 addresses have dots; bare hostnames without TLDs are blocked here.
        return host.includes('.') || host.includes(':');
    } catch {
        return false;
    }
}
